import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Crown, Calendar } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { getWcTeam } from "../lib/api";

const POSITION_ORDER = { GKP: 0, DEF: 1, MID: 2, FWD: 3, UNK: 4 };
const POSITION_LABEL = { GKP: "Goalkeepers", DEF: "Defenders", MID: "Midfielders", FWD: "Forwards" };

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

function PlValueBadge({ pl }) {
    if (!pl) return null;
    const isTop50 = pl.value_rank != null;
    return (
        <span
            className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                isTop50
                    ? "border-fa-mint/60 text-fa-mint bg-fa-mint/5"
                    : "border-white/15 text-white/40"
            }`}
            data-testid={`pl-badge-${pl.fpl_player_id}`}
        >
            {isTop50
                ? <>🇬🇧 PL #{pl.value_rank} · {pl.ppm} ppm</>
                : <>🇬🇧 in PL · {pl.fpl_team_name}</>}
        </span>
    );
}

function PlayerRow({ p }) {
    return (
        <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`squad-row-${p.fd_player_id}`}>
            <div className="flex items-center gap-2 min-w-0">
                {p.shirt_number != null && (
                    <span className="font-mono text-[10px] text-white/30 tabular-nums w-5">#{p.shirt_number}</span>
                )}
                <span className="font-display font-black text-sm truncate">{p.name}</span>
            </div>
            <div className="flex items-center gap-2">
                <PlValueBadge pl={p.pl_value} />
            </div>
        </div>
    );
}

function PositionGroup({ pos, players }) {
    if (!players.length) return null;
    return (
        <section className="border border-white/10 bg-[#0d0d0d] p-3" data-testid={`pos-group-${pos}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-2">
                {POSITION_LABEL[pos] || pos} · {players.length}
            </div>
            {players.map((p) => <PlayerRow key={p.fd_player_id} p={p} />)}
        </section>
    );
}

function FixtureRow({ m, teamId }) {
    const isHome = m.home_team_id === teamId;
    const opp = isHome ? m.away_team_name : m.home_team_name;
    const venue = isHome ? "H" : "A";
    const stageBadge = m.stage === "GROUP_STAGE"
        ? `MD${m.matchday} · ${m.group?.replace("GROUP_", "Group ") || "?"}`
        : m.stage?.replace(/_/g, " ");
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0" data-testid={`fixture-${m.fd_match_id}`}>
            <div className="flex-1 min-w-0">
                <div className="font-mono text-[11px]">
                    <span className="text-white/40 mr-1">vs</span>
                    <span className="text-white">{opp}</span>
                    <span className="text-white/40 ml-1">({venue})</span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/30">
                    {stageBadge}
                </div>
            </div>
            <div className="font-mono text-[10px] text-white/50 flex items-center gap-1">
                <Calendar size={10} weight="fill" />
                {fmtKick(m.utc_date)}
            </div>
        </div>
    );
}

export default function WorldCupTeam() {
    const { teamId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getWcTeam(teamId)
            .then(setData)
            .catch(() => toast.error("Team not found"))
            .finally(() => setLoading(false));
    }, [teamId]);

    if (loading || !data) return (
        <AppShell>
            <div className="font-mono text-[12px] text-white/40">Loading team…</div>
        </AppShell>
    );

    const { team, squad, fixtures, pl_player_count: plCount, pl_top_value_count: plTop } = data;
    const byPos = { GKP: [], DEF: [], MID: [], FWD: [], UNK: [] };
    [...squad]
        .sort((a, b) => (POSITION_ORDER[a.position] ?? 9) - (POSITION_ORDER[b.position] ?? 9))
        .forEach((p) => {
            if (byPos[p.position] != null) byPos[p.position].push(p);
            else byPos.UNK.push(p);
        });

    return (
        <AppShell>
            <section className="space-y-5">
                <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                </Link>

                {/* Header */}
                <header className="border-2 border-fa-violet/40 bg-[#0d0d0d] p-6" data-testid="wc-team-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-violet flex items-center gap-2">
                        <Trophy size={11} weight="fill" />
                        {team.group?.replace("GROUP_", "Group ") || "Group ?"}
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight mt-2">
                        {team.name}
                    </h1>
                    <p className="font-mono text-[12px] text-white/60 mt-2">
                        Coach: <span className="text-white">{team.coach || "—"}</span>
                        {team.squad_size ? ` · ${team.squad_size} players` : ""}
                    </p>
                    {plCount > 0 && (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint mt-3 flex items-center gap-2 flex-wrap">
                            <Crown size={11} weight="fill" />
                            <span data-testid="pl-summary">
                                {plCount} PL players · {plTop} in Top-50 value
                            </span>
                        </div>
                    )}
                </header>

                {/* Squad */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wc-squad-grid">
                    {["GKP", "DEF", "MID", "FWD", "UNK"].map((pos) => (
                        <PositionGroup key={pos} pos={pos} players={byPos[pos]} />
                    ))}
                </div>

                {/* Fixtures */}
                {fixtures?.length > 0 && (
                    <section className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="wc-fixtures-section">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2">
                            Fixtures · {fixtures.length}
                        </div>
                        {fixtures.map((m) => <FixtureRow key={m.fd_match_id} m={m} teamId={team.fd_team_id} />)}
                    </section>
                )}
            </section>
        </AppShell>
    );
}
